import clsx from 'clsx';

/**
 * Loading-state placeholders. Use these instead of bare spinners for any
 * content area >100ms so users see structure right away.
 *
 *   <SkeletonLine className="w-1/2" />
 *   <SkeletonCard />
 *   <SkeletonTable rows={5} cols={4} />
 */

export function SkeletonLine({ className = '' }) {
  return (
    <div
      className={clsx(
        'animate-pulse rounded-md bg-gradient-to-r from-gray-100 via-gray-200/70 to-gray-100 h-3.5',
        className,
      )}
    />
  );
}

export function SkeletonCard({ className = '', lines = 3, wrap = true }) {
  return (
    <div className={clsx(wrap && 'card', className)}>
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse" />
        <div className="flex-1 space-y-2.5">
          <SkeletonLine className="w-1/3" />
          {Array.from({ length: lines }).map((_, i) => (
            <SkeletonLine
              key={i}
              className={i === lines - 1 ? 'w-2/3' : 'w-full'}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 4, cols = 4, className = '', wrap = true }) {
  return (
    <div className={clsx(wrap && 'card', className)}>
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r} className="flex gap-3">
            {Array.from({ length: cols }).map((_, c) => (
              <SkeletonLine
                key={c}
                className={clsx(
                  'flex-1',
                  c === 0 ? 'h-4' : 'h-3.5',
                )}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SkeletonGrid({ count = 4, className = '' }) {
  return (
    <div className={clsx('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} lines={2} />
      ))}
    </div>
  );
}

/**
 * Dark-gradient hero placeholder for tabs that load a hero banner. Matches
 * the visual weight of the real hero so the page doesn't reflow when data
 * arrives.
 */
export function SkeletonHero({ className = '' }) {
  return (
    <div
      className={clsx(
        'relative overflow-hidden rounded-2xl bg-ink-gradient p-6 sm:p-8 shadow-xl',
        className,
      )}
    >
      <div className="absolute inset-0 opacity-30 bg-brand-radial" />
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-600 blur-3xl opacity-20" />
      <div className="relative flex items-start gap-4">
        <div className="h-11 w-11 rounded-xl bg-white/10 animate-pulse" />
        <div className="flex-1 space-y-3">
          <div className="h-3 w-24 rounded bg-white/10 animate-pulse" />
          <div className="h-7 w-2/3 rounded-md bg-white/15 animate-pulse" />
          <div className="h-3.5 w-3/4 rounded bg-white/10 animate-pulse" />
        </div>
      </div>
    </div>
  );
}

/**
 * List of N rows for cases where a card body contains a vertical list.
 */
export function SkeletonList({ rows = 4, className = '', wrap = true }) {
  return (
    <div className={clsx(wrap && 'card', className)}>
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse" />
            <div className="flex-1 space-y-2">
              <SkeletonLine className="w-1/2" />
              <SkeletonLine className="w-3/4" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Full-page placeholder: hero + 4-card stat row + content area. Use as the
 * loading state for any tab that has a hero on top.
 */
export function SkeletonPage({ stats = 4, withTable = false, className = '' }) {
  return (
    <div className={clsx('space-y-6', className)}>
      <SkeletonHero />
      <SkeletonGrid count={stats} />
      {withTable ? <SkeletonTable rows={5} cols={4} /> : <SkeletonCard lines={4} />}
    </div>
  );
}

export default SkeletonLine;
